import "./styles.scss";
import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { Formik, Field, ErrorMessage } from "formik";
import { useNavigate } from "react-router";
import { v4 as uuid } from "uuid";
import { toast } from 'react-toastify';
import logo from "../../../images/logo-trans.png";
import { signup } from "../../../services/users";
import { fetchActiveCountryOptions } from "../../../services/countries";
import BeatLoader from "react-spinners/BeatLoader";
import Constant from "../../../Constant";
import { ref, storage } from "../../../firebase/firebase-config";
import { getDownloadURL, uploadBytes } from "firebase/storage";
import { uploadKybFile } from "../../../utils/kyb-upload";

// const phoneRegex = /^\+[1-9]{1}[0-9]{0,2}\d{9,15}$/;
const phoneRegex = /^([0-9]+)[0-9]{9,}/;
const pinRegex = /[0-9]{5}/;


const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const normalizePersonName = (value) =>
  `${value || ""}`.toLowerCase().replace(/\s+/g, " ").trim();

const expectedMdName = (firstName, lastName) =>
  normalizePersonName(`${firstName || ""} ${lastName || ""}`);

const merchantRequired = (schema, message) =>
  Yup.string().when("role", {
    is: "Merchant",
    then: schema.required(message),
    otherwise: Yup.string(),
  });

const signupSchema = Yup.object().shape({
  email: Yup.string()
    .email("Enter a valid email address")
    .required("Email is required"),

  first_name: Yup.string().required("Field is required"),
  last_name: Yup.string().when("role", {
    is: "Merchant",
    then: Yup.string().required("Last name is required for merchant KYB"),
    otherwise: Yup.string(),
  }),

  phone_number: Yup.string()
    .required("Phone is required")
    .min(10, "Phone number is too short - Should be 10 digits minimum")
    .matches(
      phoneRegex,
      "Include country code. Ex 23767xxxxx.."
    ),

  pin: Yup.string()
    .required("PIN is required")
    .min(5, "PIN is too short - Should be 5 digits")
    .max(5, "Should be 5 digits")
    .matches(pinRegex, "Only digits allowed"),

  confirm_pin: Yup.string()
    .required("PIN is required")
    .min(5, "PIN is too short - Should be 5 digits")
    .max(5, "Should be 5 digits")
    .matches(pinRegex, "Only digits allowed"),
  terms_privacy: Yup.boolean()
    .required(
      "You have to accept to the terms and privacy policies to continue"
    )
    .oneOf([true], "Terms and privacy must be accepted."),
  business_registration_number: merchantRequired(
    Yup.string(),
    "Business registration number is required"
  ),
  business_registration_expiry: Yup.string().when("role", {
    is: "Merchant",
    then: Yup.string()
      .required("Business registration expiry is required")
      .test(
        "not-expired",
        "Business registration document is expired",
        (value) => value && new Date(value) >= startOfToday()
      ),
    otherwise: Yup.string(),
  }),
  business_registration_doc: merchantRequired(
    Yup.string(),
    "Business registration document is required"
  ),
  md_name: Yup.string().when(["role", "first_name", "last_name"], {
    is: (role) => role === "Merchant",
    then: Yup.string()
      .required("MD full name is required")
      .test(
        "matches-signer",
        "MD name must match the first and last name on this form",
        function (value) {
          const expected = expectedMdName(
            this.parent.first_name,
            this.parent.last_name
          );
          return expected && normalizePersonName(value) === expected;
        }
      ),
    otherwise: Yup.string(),
  }),
  md_id_expiry: Yup.string().when("role", {
    is: "Merchant",
    then: Yup.string()
      .required("MD ID expiry is required")
      .test(
        "not-expired",
        "MD ID is expired",
        (value) => value && new Date(value) >= startOfToday()
      ),
    otherwise: Yup.string(),
  }),
  md_selfie_doc: merchantRequired(Yup.string(), "MD selfie ID is required"),
  poa_document_date: Yup.string().when("role", {
    is: "Merchant",
    then: Yup.string()
      .required("Proof of address document date is required")
      .test("not-stale", "Proof of address must be dated within the last 90 days", (value) => {
        if (!value) return false;
        const date = new Date(value);
        const min = startOfToday();
        min.setDate(min.getDate() - 90);
        return date >= min && date <= startOfToday();
      }),
    otherwise: Yup.string(),
  }),
  proof_of_address_doc: merchantRequired(
    Yup.string(),
    "Proof of address document is required"
  ),
  tax_id: merchantRequired(Yup.string(), "Tax ID is required"),
});

const rolesOptions = [
  { value: "Personal", label: "Personal" },
  { value: "Merchant", label: "Merchant" },
  { value: "Agent", label: "Agent" },
];

const languageOptions = [
  { value: "English", label: "English" },
  { value: "French", label: "French" },
];

const SignupForm = ({ userId }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(logo);
  const [image, setImage] = useState("");
  const [kybUploading, setKybUploading] = useState({
    business_registration_doc: false,
    md_selfie_doc: false,
    proof_of_address_doc: false,
  });

  const isKybUploading = Object.values(kybUploading).some(Boolean);
  const [countryOptions, setCountryOptions] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [countriesError, setCountriesError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setCountriesLoading(true);
    setCountriesError("");
    fetchActiveCountryOptions()
      .then((options) => {
        if (!cancelled) setCountryOptions(options);
      })
      .catch((err) => {
        if (!cancelled) {
          setCountryOptions([]);
          setCountriesError(err.message || "Could not load countries");
        }
      })
      .finally(() => {
        if (!cancelled) setCountriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const uploadKybDocument = (file, docType, setFieldValue, fieldName) => {
    if (!file) return;
    setKybUploading((prev) => ({ ...prev, [fieldName]: true }));
    uploadKybFile(file, docType)
      .then((url) => {
        setFieldValue(fieldName, url);
      })
      .catch((err) => {
        console.error("KYB upload failed", err);
        setFieldValue(fieldName, "");
        toast.error(
          err?.message && !`${err.message}`.includes("Firebase")
            ? err.message
            : "Failed to upload KYB document, please try again."
        );
      })
      .finally(() => {
        setKybUploading((prev) => ({ ...prev, [fieldName]: false }));
      });
  };

  const handleImageChange = (event) => {
    setImageUploading(true);

    const file = event.target.files[0];
    const fileName = file.name;

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };

      reader.readAsDataURL(file);
    } else {
      setImageUploading(false);

      return;
    }

    const extension = fileName.substr((~-fileName.lastIndexOf(".") >>> 0) + 2);

    const unique_id = uuid();

    const imageRef = ref(storage, `id_docs/${unique_id}.${extension}`);

    uploadBytes(imageRef, file, {
      contentType: `image/${extension}`,
    })
      .then(() => {
        getDownloadURL(imageRef)
          .then((url) => {
            setImage(url);
          })
          .catch(() => {
            setErrorMessage(
              "Error uploading image file for verification. Please try again later"
            );
          });
      })
      .catch(() => {
        setErrorMessage(
          "Error uploading image file for verification. Please try again later"
        );
      })
      .finally(() => setImageUploading(false));
  };

  const handleFormSubmit = (values) => {
    setIsLoading(true);
    signup(values)
      .then((res) => {
        // setSuccessMessage(res.message);
        toast(`${res.message}.`);
        if(res.status_code == 200 || res.status_code == 201){
          setSuccessMessage(res.message);
          setTimeout( _ => navigate('/') , 5000 );
        }
        else
          setErrorMessage(res.message);
        // navigate("/");
      })
      .catch((err) => {
        setErrorMessage(err.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className="login-form">
      Complete your Signup
      <div className="login-error">{errorMessage ? errorMessage : ""}</div>
      <div className="login-success">
        {successMessage ? successMessage : ""}
      </div>
      { !successMessage && 
      <Formik
        initialValues={{
          email: "",
          first_name: "",
          last_name: "",
          phone_number: "",
          address: "",
          city: "",
          country: "",
          role: "",
          language: "",
          pin: "",
          confirm_pin: "",
          terms_privacy: false,
          business_registration_number: "",
          business_registration_expiry: "",
          business_registration_doc: "",
          md_name: "",
          md_id_expiry: "",
          md_selfie_doc: "",
          poa_document_date: "",
          proof_of_address_doc: "",
          tax_id: "",
        }}
        onSubmit={(values) => {
          if (values.role === "Merchant" && isKybUploading) {
            toast.warning("Wait for KYB documents to finish uploading");
            return;
          }
          handleFormSubmit({ ...values, image, id: userId });
        }}
        validationSchema={signupSchema}
      >
        {({ handleSubmit, values, handleChange, errors, touched, setFieldValue }) => (
          <form onSubmit={handleSubmit} id="signupform">
            <div className={`field`}>
              <label htmlFor="email">email</label>
              <Field
                name="email"
                id="email"
                placeholder="Enter Email"
                value={values.email}
                onChange={handleChange}
              />
              {errors.email && touched.email ? (
                <div className="error">{errors.email}</div>
              ) : null}
            </div>
            <div className={`field`}>
              <label htmlFor="first_name">First name</label>
              <Field
                name="first_name"
                id="first_name"
                placeholder="Enter First name"
                value={values.first_name}
                onChange={handleChange}
              />
              {errors.first_name && touched.first_name ? (
                <div className="error">{errors.first_name}</div>
              ) : null}
            </div>
            <div className={`field`}>
              <label htmlFor="last_name">Last name</label>
              <Field
                name="last_name"
                id="last_name"
                placeholder="Enter Last name"
                value={values.last_name}
                onChange={handleChange}
              />
              {errors.last_name && touched.last_name ? (
                <div className="error">{errors.last_name}</div>
              ) : null}
            </div>
            <div className={`field`}>
              <label htmlFor="phone_number">Phone Number</label>
              <Field
                name="phone_number"
                id="phone_number"
                type="tel"
                placeholder="Enter Phone mumber"
                value={values.phone_number}
                onChange={handleChange}
              />
              {errors.phone_number && touched.phone_number ? (
                <div className="error">{errors.phone_number}</div>
              ) : null}
            </div>
            <div className={`field`}>
              <label htmlFor="address">Address</label>
              <Field
                name="address"
                id="address"
                placeholder="Enter Address"
                value={values.address}
                onChange={handleChange}
              />
              {errors.address && touched.address ? (
                <div className="error">{errors.address}</div>
              ) : null}
            </div>
            <div className={`field`}>
              <label htmlFor="city">City</label>
              <Field
                name="city"
                id="city"
                placeholder="Enter City"
                value={values.city}
                onChange={handleChange}
              />
              {errors.city && touched.city ? (
                <div className="error">{errors.city}</div>
              ) : null}
            </div>
            <div className={`field`}>
              <label htmlFor="country">Select Country</label>
              <Field
                name="country"
                id="country"
                as="select"
                value={values.country}
                onChange={handleChange}
                disabled={countriesLoading}
                className="__form-input"
              >
                <option value="">
                  {countriesLoading ? "Loading countries…" : "Select a country"}
                </option>
                {countryOptions.map((country) => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </Field>
              {countriesError ? (
                <div className="error">{countriesError}</div>
              ) : null}
              {errors.country && touched.country ? (
                <div className="error">{errors.country}</div>
              ) : null}
            </div>
            <div className={`field`}>
              <label htmlFor="role">Account type</label>
              <Field
                name="role"
                as="select"
                id={`role`}
                value={values.role}
                onChange={handleChange}
                className="__form-input"
              >
                <option value="">Select a role</option>
                {rolesOptions.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </Field>
              {errors.role && touched.role ? (
                <div className="error">{errors.role}</div>
              ) : null}
            </div>
            {values.role === "Merchant" && (
              <div className="kyb-section">
                <p className="kyb-heading">KYB documents</p>
                <p className="kyb-help">
                  Required for merchant accounts. JPEG, PNG, or PDF, max 5MB.
                  Registration and MD ID must not be expired. Proof of address
                  must be dated within 90 days. MD name must match the signer.
                </p>
                <div className={`field`}>
                  <label htmlFor="business_registration_number">
                    Business registration number
                  </label>
                  <Field
                    name="business_registration_number"
                    id="business_registration_number"
                    placeholder="Registration / incorporation number"
                    value={values.business_registration_number}
                    onChange={handleChange}
                  />
                  {errors.business_registration_number &&
                  touched.business_registration_number ? (
                    <div className="error">
                      {errors.business_registration_number}
                    </div>
                  ) : null}
                </div>
                <div className={`field`}>
                  <label htmlFor="business_registration_expiry">
                    Business registration expiry
                  </label>
                  <Field
                    name="business_registration_expiry"
                    id="business_registration_expiry"
                    type="date"
                    value={values.business_registration_expiry}
                    onChange={handleChange}
                  />
                  {errors.business_registration_expiry &&
                  touched.business_registration_expiry ? (
                    <div className="error">
                      {errors.business_registration_expiry}
                    </div>
                  ) : null}
                </div>
                <div className={`field`}>
                  <label htmlFor="business_registration_file">
                    Business registration document
                  </label>
                  <input
                    type="file"
                    id="business_registration_file"
                    accept="image/png,image/jpeg,image/jpg,application/pdf"
                    onChange={(event) =>
                      uploadKybDocument(
                        event.target.files[0],
                        "business_reg",
                        setFieldValue,
                        "business_registration_doc"
                      )
                    }
                  />
                  {kybUploading.business_registration_doc && (
                    <BeatLoader
                      color={Constant.spinner_color}
                      loading
                      cssOverride={{ textAlign: "center", marginTop: "12px" }}
                    />
                  )}
                  {values.business_registration_doc && (
                    <p className="kyb-uploaded">Document uploaded</p>
                  )}
                  {errors.business_registration_doc &&
                  touched.business_registration_doc ? (
                    <div className="error">{errors.business_registration_doc}</div>
                  ) : null}
                </div>
                <div className={`field`}>
                  <label htmlFor="md_name">Selfie ID of MD — full name</label>
                  <Field
                    name="md_name"
                    id="md_name"
                    placeholder="Must match first and last name"
                    value={values.md_name}
                    onChange={handleChange}
                  />
                  {errors.md_name && touched.md_name ? (
                    <div className="error">{errors.md_name}</div>
                  ) : null}
                </div>
                <div className={`field`}>
                  <label htmlFor="md_id_expiry">MD ID expiry</label>
                  <Field
                    name="md_id_expiry"
                    id="md_id_expiry"
                    type="date"
                    value={values.md_id_expiry}
                    onChange={handleChange}
                  />
                  {errors.md_id_expiry && touched.md_id_expiry ? (
                    <div className="error">{errors.md_id_expiry}</div>
                  ) : null}
                </div>
                <div className={`field`}>
                  <label htmlFor="md_selfie_file">Selfie ID of MD</label>
                  <input
                    type="file"
                    id="md_selfie_file"
                    accept="image/png,image/jpeg,image/jpg,application/pdf"
                    onChange={(event) =>
                      uploadKybDocument(
                        event.target.files[0],
                        "md_selfie",
                        setFieldValue,
                        "md_selfie_doc"
                      )
                    }
                  />
                  {kybUploading.md_selfie_doc && (
                    <BeatLoader
                      color={Constant.spinner_color}
                      loading
                      cssOverride={{ textAlign: "center", marginTop: "12px" }}
                    />
                  )}
                  {values.md_selfie_doc && (
                    <p className="kyb-uploaded">Document uploaded</p>
                  )}
                  {errors.md_selfie_doc && touched.md_selfie_doc ? (
                    <div className="error">{errors.md_selfie_doc}</div>
                  ) : null}
                </div>
                <div className={`field`}>
                  <label htmlFor="poa_document_date">
                    Proof of address document date
                  </label>
                  <Field
                    name="poa_document_date"
                    id="poa_document_date"
                    type="date"
                    value={values.poa_document_date}
                    onChange={handleChange}
                  />
                  {errors.poa_document_date && touched.poa_document_date ? (
                    <div className="error">{errors.poa_document_date}</div>
                  ) : null}
                </div>
                <div className={`field`}>
                  <label htmlFor="proof_of_address_file">Proof of address</label>
                  <input
                    type="file"
                    id="proof_of_address_file"
                    accept="image/png,image/jpeg,image/jpg,application/pdf"
                    onChange={(event) =>
                      uploadKybDocument(
                        event.target.files[0],
                        "poa",
                        setFieldValue,
                        "proof_of_address_doc"
                      )
                    }
                  />
                  {kybUploading.proof_of_address_doc && (
                    <BeatLoader
                      color={Constant.spinner_color}
                      loading
                      cssOverride={{ textAlign: "center", marginTop: "12px" }}
                    />
                  )}
                  {values.proof_of_address_doc && (
                    <p className="kyb-uploaded">Document uploaded</p>
                  )}
                  {errors.proof_of_address_doc &&
                  touched.proof_of_address_doc ? (
                    <div className="error">{errors.proof_of_address_doc}</div>
                  ) : null}
                </div>
                <div className={`field`}>
                  <label htmlFor="tax_id">Tax ID</label>
                  <Field
                    name="tax_id"
                    id="tax_id"
                    placeholder="TIN / VAT / tax identification number"
                    value={values.tax_id}
                    onChange={handleChange}
                  />
                  {errors.tax_id && touched.tax_id ? (
                    <div className="error">{errors.tax_id}</div>
                  ) : null}
                </div>
              </div>
            )}
            <div className={`field`}>
              <label htmlFor="language">Language</label>
              <Field
                name="language"
                as="select"
                id={`language`}
                value={values.language}
                onChange={handleChange}
                className="__form-input"
              >
                <option value="">Select a Language</option>
                {languageOptions.map((language) => (
                  <option key={language.value} value={language.value}>
                    {language.label}
                  </option>
                ))}
              </Field>
              {errors.language && touched.language ? (
                <div className="error">{errors.language}</div>
              ) : null}
            </div>
            <div className="field">
              <label htmlFor="image">
                Upload your ID (Government issued ID/Passport)*
              </label>
              <img
                src={imagePreview}
                alt="ID Image preview"
                style={{
                  width: "100%",
                  maxWidth: "150px",
                  maxHeight: "200px",
                  margin: "2px 0",
                }}
              />
              <Field
                type="file"
                id="image"
                accept="image/png,image/jpeg,image/jpg"
                name="image"
                onChange={handleImageChange}
              />
              {values.image && values.image.length > 0 && (
                <ErrorMessage name="image" className="error" component="div" />
              )}
              <BeatLoader
                color={Constant.spinner_color}
                loading={imageUploading}
                cssOverride={{
                  textAlign: "center",
                  marginTop: "20px",
                }}
              />
              {errors.image && errors.image.length > 0 && (
                <ErrorMessage name="image" className="error" component="div" />
              )}
            </div>
            <div className={`field`}>
              <label htmlFor="pin">PIN <small className="login-error">(*Avoid same digit pins like 00000)</small></label>
              <Field
                name="pin"
                id="pin"
                type="password"
                placeholder="Enter PIN"
                value={values.pin}
                maxLength={5}
                onChange={handleChange}
              />

              {errors.pin && touched.pin ? (
                <div className="error">{errors.pin}</div>
              ) : null}
            </div>
            <div className={`field`}>
              <label htmlFor="confirm_pin">Confirm PIN</label>
              <Field
                name="confirm_pin"
                id="confirm_pin"
                type="password"
                placeholder="Confirm PIN"
                value={values.confirm_pin}
                onChange={handleChange}
                maxLength={5}
              />
              {errors.confirm_pin && touched.confirm_pin ? (
                <div className="error">{errors.confirm_pin}</div>
              ) : null}
            </div>
            <div className={`field`}>
              <div className="terms" htmlFor="terms_privacy">
                <Field
                  name="terms_privacy"
                  id="terms_privacy"
                  type="checkbox"
                  className="checkbox"
                />
                I accept the
                <a href="https://aftawallet.com/terms.html">Terms of Use</a> and
                <a href="https://aftawallet.com/privacy.html">Privacy policy</a>
              </div>

              {errors.terms_privacy && touched.terms_privacy ? (
                <div className="error">{errors.terms_privacy}</div>
              ) : null}
            </div>
            <button type="submit" disabled={isLoading || isKybUploading}>
              {!isLoading && <span>Signup</span>}
              {isLoading && <span>Signing up. Please wait ...</span>}
              <BeatLoader
                color={Constant.spinner_color}
                loading={isLoading}
                cssOverride={{
                  textAlign: "center",
                  marginTop: "20px",
                }}
              />
            </button>
          </form>
        )}
      </Formik>
      }
      <p className="to_home">
        Back to
        <a navigate="/home">Home</a>
      </p>
    </div>
  );
};
export default SignupForm;
